import { parseAbi } from "viem"
import { z } from "zod"
import {
  type GachaEvmChain,
  gachaEvmAddressSchema,
  gachaEvmChainSchema,
  getGachaPreferredToken,
} from "./evm-chains"

export const gachaEvmHashSchema = z
  .string()
  .regex(/^0x[0-9a-fA-F]{64}$/)
  .transform((value): `0x${string}` => `0x${value.slice(2).toLowerCase()}`)
const units = z.string().regex(/^\d+$/)
export const gachaEvmReservationSchema = z.object({
  memo: z.string().min(1),
  chainId: z.number().int(),
  token: z.object({
    key: z.string(),
    symbol: z.string(),
    address: gachaEvmAddressSchema,
    decimals: z.number().int(),
  }),
  usdc: gachaEvmAddressSchema,
  usdcDecimals: z.number().int(),
  paymentContract: gachaEvmAddressSchema,
  amount: units.refine((value) => BigInt(value) > BigInt(0)),
})
export function validateGachaEvmReservation(
  chain: GachaEvmChain,
  reservation: z.infer<typeof gachaEvmReservationSchema>,
) {
  const token = getGachaPreferredToken(chain)
  if (
    reservation.chainId !== chain.chainId ||
    reservation.paymentContract.toLowerCase() !==
      chain.paymentContract.toLowerCase() ||
    reservation.token.address.toLowerCase() !== token.address.toLowerCase() ||
    reservation.usdc.toLowerCase() !== token.address.toLowerCase() ||
    reservation.token.key !== token.key ||
    reservation.token.symbol !== token.symbol ||
    reservation.token.decimals !== token.decimals ||
    reservation.usdcDecimals !== token.decimals
  )
    throw new Error(
      "Reservation does not match approved chain, token or payment contract",
    )
  return reservation
}
// Only documented upstream fields. Optional metadata is not proof of delivery.
export const gachaEvmOpenSchema = z.object({
  success: z.boolean().optional(),
  code: z.string().optional(),
  status: z.string().optional(),
  retryAfterMs: z.number().nonnegative().optional(),
  reason: z.string().optional(),
  memo: z.string().optional(),
  chain_id: z.number().int().optional(),
  evm_contract_address: gachaEvmAddressSchema.nullable().optional(),
  evm_token_id: z
    .union([units, z.number().int().nonnegative().safe().transform(String)])
    .nullable()
    .optional(),
  rarity: z.enum(["common", "uncommon", "rare", "epic"]).optional(),
  roll: z.number().optional(),
  prize_tier: z.number().int().optional(),
  rarity_label: z.string().optional(),
  chain_key: z.string().optional(),
  card_name: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined),
  nft_address: z.string().nullable().optional(),
  nftWon: z
    .object({
      name: z.string().optional(),
      image: z.string().optional(),
      insured_value: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough()
    .nullable()
    .optional(),
  insured_value: z.union([z.string(), z.number()]).optional(),
  buyback_amount: z.union([z.string(), z.number()]).optional(),
  transaction_signature: z.string().nullable().optional(),
  transactionSignature: z.string().nullable().optional(),
})
export type GachaEvmOpen = z.infer<typeof gachaEvmOpenSchema>
export function isGachaEvmDelivered(
  httpStatus: number,
  result: GachaEvmOpen,
  record: { memo?: string; chain: GachaEvmChain },
) {
  if (
    httpStatus !== 200 ||
    result.success !== true ||
    result.evm_token_id == null
  )
    return false
  if (
    result.memo !== record.memo ||
    result.chain_id !== record.chain.chainId ||
    result.evm_contract_address?.toLowerCase() !==
      record.chain.cardContract.toLowerCase()
  )
    throw new Error("Delivered Issued Pack identity mismatch")
  return true
}
export const gachaEvmEvidenceSchema = z.object({
  hash: gachaEvmHashSchema,
  blockNumber: units,
  blockHash: gachaEvmHashSchema,
  chainId: z.number().int(),
  verifiedAt: z.string(),
  confirmations: z.number().int().positive(),
})
export const gachaEvmPurchaseSchema = z.object({
  id: z.string(),
  requestId: z.string(),
  userId: z.string(),
  walletAddress: gachaEvmAddressSchema,
  environment: z.enum(["production", "development"]),
  upstream: z.string().url(),
  chain: gachaEvmChainSchema,
  machineCode: z.string(),
  createdAt: z.string(),
  openedAt: z.string().datetime().optional(),
  memo: z.string().optional(),
  amount: units.optional(),
  state: z.enum([
    "reserving",
    "reserved",
    "payment_pending",
    "payment_failed",
    "payment_rejected",
    "paid",
    "fulfilling",
    "delivered",
    "unpaid_cancelled",
    "unpaid_expired",
    "needs_support",
  ]),
  active: z.boolean(),
  evidence: gachaEvmEvidenceSchema.optional(),
  reportedHash: gachaEvmHashSchema.optional(),
  // Canonically reverted attempts are retained, but do not prove a memo unpaid.
  revertedHashes: z.array(gachaEvmHashSchema).optional(),
  award: gachaEvmOpenSchema.optional(),
  retryAfterMs: z.number().optional(),
  lastCode: z.string().optional(),
  support: z
    .object({
      reason: z.enum([
        "local_reconciliation_budget_exhausted",
        "paid_reservation_expired",
        "late_broadcast_after_rejection",
      ]),
      escalatedAt: z.string(),
      payment: z.enum(["verified", "unknown"]),
      policy: z.literal("operator-escalation-v1"),
    })
    .optional(),
})
export type GachaEvmPurchase = z.infer<typeof gachaEvmPurchaseSchema>
export const gachaEvmPayAbi = parseAbi([
  "function pay(address token, uint256 amount, string memo)",
])
// Accept only the deployed receipt layout: payer and token are indexed.
export const gachaEvmPaidEventAbi = parseAbi([
  "event Paid(address indexed from, address indexed token, uint256 amount, string memo)",
])
